import { Action } from '~/lib/ApiModel';
import React from 'react';
import { Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import DialogModal from '~/components/DialogModal';
import { hasTerminated, needsInteraction } from '~/lib/helper/ActionHelper';
import ActionMonitor from '~/components/actions/ActionMonitor';
import MinimizeButton from '~/components/MinimizeButton';
import { observer } from 'mobx-react';
import { AsyncButton } from '../AsyncButton';
import dayjs from 'dayjs';

type TrackedActionProps = {
    action: Action;
    // `Promise<void>` (not `void`/`unknown`) makes a block-bodied arrow that drops the promise a
    // compile error — see PanelSite.md's "Never `void` the promise in an AsyncButton onClick" rule.
    proceed: (action: Action) => Promise<void>;
    abort: (action: Action) => Promise<void>;
    onClose: () => void;
};

type TrackedActionState = {
    elapsedText: string;
};

@observer
export default class TrackedActionModal extends React.Component<TrackedActionProps, TrackedActionState> {
    private timerInterval?: ReturnType<typeof setInterval>;

    state: TrackedActionState = {
        elapsedText: '',
    };

    componentDidMount() {
        this.updateElapsed();
        this.timerInterval = setInterval(() => this.updateElapsed(), 1000);
    }

    componentWillUnmount() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    }

    updateElapsed() {
        if (hasTerminated(this.props.action)) {
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = undefined;
            }
            return;
        }

        const now = dayjs();
        const serverStart = dayjs(this.props.action.started_at);
        const started = serverStart.isAfter(now) ? now : serverStart;
        const totalSeconds = now.diff(started, 's');
        const hours = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        const parts: string[] = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0 || hours > 0) parts.push(`${mins}m`);
        parts.push(`${secs}s`);

        this.setState({ elapsedText: parts.join(' ') });
    }

    renderUserInteractionRequest(action: Action) {
        const interactionType = action.user_interaction_required?.interaction_type;

        switch (interactionType) {
            case 'allow-unclean-power-off':
                return (
                    <>
                        <ModalBody>
                            <p>
                                A "Power Off" request was sent to your server, but it has not yet shut down. The
                                operating system may still be completing a clean shutdown.
                            </p>
                            <p className="text-muted small mb-0">
                                <strong>Continue Waiting</strong> - Minimize this dialog and check the console to see if
                                the server is still shutting down.
                                <br />
                                <strong>Abort</strong> - Cancel the operation. The server will remain in its current
                                state.
                                <br />
                                <strong>Force Shutdown</strong> - Immediately power off the server. This may cause data
                                loss if the operating system is still shutting down.
                            </p>
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={() => this.props.onClose()}>Continue Waiting</Button>
                            <AsyncButton
                                color="secondary"
                                errorSummary="Unable to abort operation"
                                onClick={() => this.abortAction(action)}
                            >
                                Abort
                            </AsyncButton>
                            <AsyncButton
                                color="danger"
                                errorSummary="Unable to force-shutdown server"
                                onClick={() => this.proceedAction(action)}
                            >
                                Force Shutdown
                            </AsyncButton>
                        </ModalFooter>
                    </>
                );

            case 'continue-after-ping-failure':
                return (
                    <>
                        <ModalBody>
                            <p>
                                Your server should have finished setup but has not responded to ping. You can check the
                                console to see the current state of the server.
                            </p>
                            <p className="text-muted small mb-0">
                                <strong>Continue Waiting</strong> - Minimize this dialog and check the console.
                                <br />
                                <strong>Abort</strong> - Cancel the operation. The server will remain in its current
                                state.
                                <br />
                                <strong>Continue</strong> - Proceed with the operation even though the server has not
                                responded.
                            </p>
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={() => this.props.onClose()}>Continue Waiting</Button>
                            <AsyncButton
                                color="secondary"
                                errorSummary="Unable to abort operation"
                                onClick={() => this.abortAction(action)}
                            >
                                Abort
                            </AsyncButton>
                            <AsyncButton
                                color="primary"
                                errorSummary="Unable to continue operation"
                                onClick={() => this.proceedAction(action)}
                            >
                                Continue
                            </AsyncButton>
                        </ModalFooter>
                    </>
                );
        }
    }

    abortAction(action: Action): Promise<void> {
        return this.props.abort(action);
    }

    proceedAction(action: Action): Promise<void> {
        return this.props.proceed(action);
    }

    render() {
        const hasStopped = hasTerminated(this.props.action);
        return (
            <DialogModal
                className="modal-lg"
                isOpen={true}
                toggle={() => this.props.onClose()}
                unmountOnClose={true}
                labelledBy="tracked-action-modal-title"
            >
                <ModalHeader
                    toggle={() => this.props.onClose()}
                    // While the action is in flight, show a "minimize" affordance that collapses the
                    // modal back to the progress banner. Once the action has stopped there is nothing
                    // to minimize to, so fall through (undefined) to reactstrap's default `btn-close`
                    // button, which renders a standard "×" with `aria-label="Close"`.
                    close={hasStopped ? undefined : <MinimizeButton onClick={() => this.props.onClose()} />}
                >
                    {/* id on the title span, not the ModalHeader — see PanelSite.md "Modals". */}
                    <span id="tracked-action-modal-title">
                        {this.props.action.title}
                        {this.props.action.status === 'completed' && ' Complete'}
                        {this.props.action.status === 'errored' && ' Failed'}
                    </span>
                    {needsInteraction(this.props.action) && this.state.elapsedText && (
                        <span className="text-muted small ms-2">({this.state.elapsedText})</span>
                    )}
                </ModalHeader>
                {needsInteraction(this.props.action) ? (
                    this.renderUserInteractionRequest(this.props.action)
                ) : (
                    <ModalBody className="pt-4">
                        <ActionMonitor
                            action={this.props.action}
                            title={
                                !hasStopped && this.state.elapsedText
                                    ? `${this.props.action.reason} (${this.state.elapsedText})`
                                    : this.props.action.reason
                            }
                        />
                    </ModalBody>
                )}
            </DialogModal>
        );
    }
}
